import { Controller } from "@hotwired/stimulus"
import { session } from "@hotwired/turbo"
import { yieldDrawerEscape } from "../lib/drawer_escape"
import { closeOverlaysIn } from "../lib/cornerstone"

// FD-279: the work-item peek is a PERSISTENT right-side overlay (rendered on every
// list surface, empty when nothing is peeked) so the drawer can be revealed + slid
// in CLIENT-SIDE the instant a row is clicked — before any network round-trip. The
// item content loads into the #peek_panel turbo-frame via a cheap, list-free request
// (Turbo-Frame: peek_panel; see PeekFrame concern), so opening/switching/closing a
// peek NEVER re-renders the work-items list. This controller owns the optimistic
// open + slide, the loading skeleton, dismissal, and focus return.
//
// The drawer is a <cs-drawer> (FD-1311): a MODAL <dialog> whose backdrop covers the whole
// page, nav included, with everything behind it inert while it is open. The drawer owns
// the slide, the backdrop, keeping focus inside, and closing on Escape or a backdrop click
// (light-dismiss). This controller owns everything Flightdeck-specific around that.
//
// Open  — a peek link (a[href*='peek=']) carries data-turbo-frame="peek_panel" +
//         data-turbo-action="advance": Turbo loads the panel into the frame and
//         advances the URL to ?peek=ID. A document-level capture listener opens the
//         drawer the moment the link is clicked (before the fetch resolves).
// Close — close button / Escape / backdrop. The close button is itself an advancing
//         frame link to the no-peek URL, so Turbo empties the frame + strips ?peek.
//         Escape and the backdrop reach us as the drawer's cs-hide (onDrawerHide), and
//         synthesize that same navigation.
// State — #peek_panel's content is the source of truth: turbo:frame-load reconciles
//         the drawer's visible state, which also covers back/forward restoration and
//         a deep-link first render.
//
// Focus return is now trivial: the list is never re-rendered, so the originating
// row/card is still in the DOM. We capture its id at click time and refocus it once
// the drawer has closed (onDrawerAfterHide). While a native modal <dialog> is open over
// the drawer (the command palette, the shortcuts help) or an editor's suggestion
// popup is showing, Escape is theirs, not the drawer's (lib/drawer_escape).
//
// FD-284: TRUE ZERO-REFETCH re-open. Re-opening an already-viewed item paints its
// previously-rendered panel straight from an in-memory LRU cache — no skeleton, no
// network request, no background revalidation (a revalidating fetch that replaced
// the frame could clobber an in-progress edit). On a cache HIT the capture-phase
// click handler preventDefault()s (aborting Turbo's frame fetch AND its URL advance)
// and we own the URL with a RAW history.pushState entry; on a MISS we fall through to
// Turbo's normal advancing frame nav and capture the result on turbo:frame-load.
// Correctness: the cache is keyed by the bare work-item id and dropped wholesale on
// any morph (broadcast refresh / self-edit redirect — a payload-free "something in
// this project changed" signal), with the still-open item re-captured fresh; it is
// also evicted on the actor's own successful in-panel write, and never captures a
// mid-edit (data-turbo-permanent) panel. See FD-284 for the rationale.

const FRAME_ID = "peek_panel"
const CACHE_MAX = 12 // FD-284: bound the per-surface panel cache (LRU).
const DEFAULT_LABEL = "Work item" // FD-1311: the drawer's name until an item's panel is in

// Origin row/card id captured at click time, consumed for focus return on dismiss.
let pendingOriginId = null

export default class extends Controller {
  static targets = ["panel", "skeleton"]

  connect() {
    this.#bindGlobalOpen()
    this.dismissed = false
    // FD-1311: whether WE want the drawer open. The drawer reports every close the same
    // way, ours included, so this is how onDrawerHide tells a user's dismissal from ours.
    this._open = false
    // FD-1311: the drawer is playing its close animation (cs-hide → cs-after-hide). It
    // can't be re-opened in that window, so a re-open waits for it (#openDrawer).
    this._drawerClosing = false
    this._reopenAfterHide = false
    this._focusOnHide = false
    // FD-284: per-surface zero-refetch cache of rendered peek panels, keyed by the
    // bare work-item id. Lives on the controller INSTANCE: it survives morph refreshes
    // (the _peek_container element is morph-preserved, so disconnect doesn't run) and
    // dies on a full Turbo Drive swap / restore-visit (disconnect) — the correct
    // surface scope, since a list navigation can change project scope.
    this.cache = new Map()
    this.currentId = null
    // FD-871: the form submissions currently in flight that STARTED inside the panel,
    // keyed by IDENTITY (Turbo's FormSubmission object) rather than by where their
    // submit-end happens to be dispatched — see onSubmitStart. Value: the panel's
    // work-item id at submit time, captured while the form is still attached.
    this._panelSubmissions = new Map()
    // FD-871: FD-579's deferred close, waiting for the in-panel write to finish. A
    // callback rather than its own turbo:submit-end listener, so there is exactly ONE
    // place that decides whether a submit-end came from inside the panel.
    this._panelSubmitWaiter = null
    // FD-853: close-navigation bookkeeping — see #restoreSupersededOpen.
    this._closePending = false
    this._closeSuperseded = false
    this._onDrawerHide = this.onDrawerHide.bind(this)
    this._onDrawerAfterHide = this.onDrawerAfterHide.bind(this)
    this._onFrameLoad = this.onFrameLoad.bind(this)
    this._onFrameRender = this.onFrameRender.bind(this)
    this._onMorph = this.onMorph.bind(this)
    this._onSubmitStart = this.onSubmitStart.bind(this)
    this._onSubmitEnd = this.onSubmitEnd.bind(this)
    this._onPopState = this.onPopState.bind(this)
    this.element.addEventListener("cs-hide", this._onDrawerHide)
    this.element.addEventListener("cs-after-hide", this._onDrawerAfterHide)
    document.addEventListener("turbo:frame-render", this._onFrameRender)
    document.addEventListener("turbo:frame-load", this._onFrameLoad)
    document.addEventListener("turbo:morph", this._onMorph)
    document.addEventListener("turbo:submit-start", this._onSubmitStart)
    document.addEventListener("turbo:submit-end", this._onSubmitEnd)
    window.addEventListener("popstate", this._onPopState)
    // FD-1311: copy the drawer's header and footer in the moment the frame's content changes,
    // in the same frame the body paints. turbo:frame-load comes 30-140ms after the render.
    this._frameObserver = new MutationObserver(() => this.#onFrameChanged())
    const frame = this.#frame()
    if (frame) this._frameObserver.observe(frame, { childList: true })
    // Reconcile the initial state to the URL (the source of truth for whether — and
    // which — peek is open): a deep-link / no-JS render, or a Turbo Drive snapshot
    // restore that REPLACED the body (re-running connect), can arrive with the panel
    // populated. If the frame already shows the URL's item, reveal + seed the cache so a
    // later close → re-open is a HIT. If the URL names a peek the frame doesn't show
    // (e.g. a late restore-visit replaced the body with a stale snapshot — FD-368),
    // reconcile to the URL's item. Otherwise stay closed: a restored snapshot can carry the
    // drawer's `open` from a page that had a peek showing.
    const peekInUrl = new URL(window.location.href).searchParams.get("peek")
    if (peekInUrl && this.#hasContent() && this.#frameItemId() === peekInUrl) {
      this.#hideSkeleton()
      this.currentId = peekInUrl // before #reveal, which syncs the drawer's chrome to it
      this.#reveal()
      this.#snapshot(peekInUrl)
    } else if (peekInUrl) {
      this.#reconcileToUrl(peekInUrl)
    } else {
      this.#closeDrawer()
    }
  }

  disconnect() {
    this.#deactivate()
    this._frameObserver?.disconnect()
    this._frameObserver = null
    this.element.removeEventListener("cs-hide", this._onDrawerHide)
    this.element.removeEventListener("cs-after-hide", this._onDrawerAfterHide)
    this._drawerClosing = false
    this._reopenAfterHide = false
    this._focusOnHide = false
    // FD-579: a pending flush-close (dirty popover submit being serialized) must not
    // leak document listeners across a full page swap.
    if (this._flushCleanup) {
      this._flushCleanup()
      this._flushCleanup = null
    }
    this._closePending = false // FD-853
    this._closeSuperseded = false
    document.removeEventListener("turbo:frame-render", this._onFrameRender)
    document.removeEventListener("turbo:frame-load", this._onFrameLoad)
    document.removeEventListener("turbo:morph", this._onMorph)
    document.removeEventListener("turbo:submit-start", this._onSubmitStart)
    document.removeEventListener("turbo:submit-end", this._onSubmitEnd)
    window.removeEventListener("popstate", this._onPopState)
    this.cache?.clear() // FD-284: surface scope — drop the cache on a full-page swap.
    // FD-871: a full page swap replaces every form we were tracking; their submit-ends
    // can never be acted on again, so don't carry the entries (or the waiter) across it.
    this._panelSubmissions?.clear()
    this._panelSubmitWaiter = null
  }

  // Optimistic open: reveal + slide the drawer in and show the skeleton the moment a
  // peek link is clicked, before the frame content arrives. (Cache MISS path.)
  open() {
    // FD-853: a re-open. If a close is outstanding — already navigating, or deferred by
    // FD-579's flush — it is now stale and must not be allowed to close what we just opened.
    if (this._closePending) this._closeSuperseded = true
    this.dismissed = false
    this.originId = pendingOriginId
    this.#showSkeleton()
    this.#reveal()
  }

  // Source-of-truth reconcile: when the #peek_panel frame finishes loading, match the
  // drawer to its content (covers content swaps and back/forward restoration).
  //
  // FD-368: the URL is the source of truth for whether a peek is OPEN — the drawer is
  // visible iff the URL carries ?peek. A frame-load that lands content while the URL
  // has NO ?peek is a stale Turbo restore-visit re-rendering the frame after a
  // back-navigation to the peekless base (our raw pushState hit-entries don't advance
  // Turbo's history index, so a pop can restore a snapshot whose frame still shows the
  // previously-viewed item). Revealing it flickers the drawer back open on a stale item
  // and races the close — so reconcile to CLOSED instead of revealing a peek at a
  // peekless URL. (A real open always has ?peek in the URL by frame-load: a MISS open
  // is an advancing frame nav whose history entry is committed before turbo:frame-load,
  // and the popstate-fallback fetch sets the frame src only after the URL is in place.)
  //
  // FD-853: one frame-load must NOT be obeyed — the response to a close navigation that a
  // re-open has already superseded. See #closeResponse and #restoreSupersededOpen.
  onFrameLoad(event) {
    if (event.target?.id !== FRAME_ID) return
    const renderedUrl = this._renderedUrl
    this._renderedUrl = null
    if (this.#closeResponse(renderedUrl)) {
      const superseded = this._closeSuperseded
      this._closePending = false
      this._closeSuperseded = false
      if (superseded && this._open) {
        if (this.#cacheHas(this.currentId)) {
          this.#restoreSupersededOpen()
          return
        }
        // FD-1311: a MISS re-open. Its own frame visit is out (the frame's src names the
        // re-opened item), and its frame-load will reveal it; the close response rendered
        // first and only emptied the panel for a moment. Hiding here closed the drawer the
        // user had just opened. Measured on peek_cache_test's open_then_close pairs: the
        // close's turbo:frame-load arrived ~130ms after its render, after the next row's
        // click had already re-pointed the frame.
        if (this.#srcPeekId() === this.currentId) return
      }
      // FD-853 residual hole, known and not closed here: the repair needs a cache entry or a
      // fetch of its own. A HIT re-open whose entry has gone by now — a morph cleared the
      // cache and #snapshot refused to re-capture (a mid-edit data-turbo-permanent panel, or
      // an open modal) — falls through to the hide below and clobbers the re-opened drawer.
      // Reproduced by clearing the cache between the re-open and the close response: drawer
      // hidden, panel emptied, ?peek gone.
    }
    const peekInUrl = new URL(window.location.href).searchParams.get("peek")
    // No ?peek in the URL → no peek should be open. Content here means a stale Turbo
    // restore-visit re-rendered the frame after a back-nav to the peekless base — hide.
    if (!peekInUrl) {
      if (!this.dismissed && this.#visible()) this.#hideImmediately()
      return
    }
    if (!this.#hasContent()) {
      if (!this.dismissed && this.#visible()) this.#hideImmediately()
      return
    }
    // Content present at a ?peek URL. If it is the item the URL names, reveal + snapshot.
    // If it's a DIFFERENT item, a stale Turbo restore-visit re-rendered the frame at one
    // of our raw pushState hit-entries (whose history index Turbo doesn't track) — the
    // URL is the source of truth, so reconcile the frame to the URL's item rather than
    // revealing the wrong panel.
    if (this.#frameItemId() === peekInUrl) {
      // FD-1311: the late frame-load of an open the user has since dismissed. Turbo can
      // dispatch an open's turbo:frame-load well after its render (measured: ~140ms, past an
      // Escape pressed in between), and it still finds the item at the ?peek URL, because
      // the close GET hasn't returned. Revealing it re-opened the drawer for a moment until
      // the close response shut it again. The old drawer flickered the same way, but the modal
      // drawer takes focus when it opens, and the second close left it on <body>. A close is
      // outstanding and nothing has re-opened since (every re-open path clears `dismissed`,
      // and open() and #injectFromCache mark the close superseded), so keep the snapshot,
      // which is a good copy, and leave the drawer closed.
      if (this.dismissed && this._closePending && !this._closeSuperseded) {
        this.#snapshot(peekInUrl)
        return
      }
      this.dismissed = false
      this.#hideSkeleton()
      this.currentId = peekInUrl // before #reveal, which syncs the drawer's chrome to it
      this.#reveal()
      // FD-284: authoritative capture — the cheap PeekFrame render (a MISS open, a
      // popstate fallback fetch, or a back/forward restore-visit re-render) just
      // landed. Snapshot it so a later re-open of this item is a zero-network HIT.
      this.#snapshot(peekInUrl)
    } else {
      this.#reconcileToUrl(peekInUrl)
    }
  }

  // FD-853: does this frame-load carry the response to a CLOSE navigation? The close is an
  // advancing frame nav to the no-peek URL, so its response is the only one that renders an
  // EMPTY panel from a peek-less frame src. (An empty panel at a ?peek src is a different
  // thing — an out-of-scope or deleted id fetched by #reconcileToUrl — and must keep
  // falling through to the hide below.)
  //
  // FD-1311: judged by the URL Turbo actually rendered (onFrameRender) when there is one. A
  // frame-load can arrive late enough that the frame's src already belongs to the next
  // open, so the src only stands in when no render URL was recorded.
  #closeResponse(renderedUrl) {
    if (renderedUrl) return !new URL(renderedUrl, window.location.href).searchParams.get("peek")
    if (this.#hasContent()) return false
    const src = this.#frame()?.getAttribute("src")
    if (!src) return false
    return !new URL(src, window.location.href).searchParams.get("peek")
  }

  // FD-1311: Turbo dispatches turbo:frame-render, carrying the response, immediately before
  // turbo:frame-load (Session#frameRendered, then #frameLoaded), so onFrameLoad reads this
  // straight back.
  onFrameRender(event) {
    if (event.target?.id !== FRAME_ID) return
    this._renderedUrl = event.detail?.fetchResponse?.location?.href || null
  }

  // The ?peek id the frame's current src asks for, or null.
  #srcPeekId() {
    const src = this.#frame()?.getAttribute("src")
    return src ? new URL(src, window.location.href).searchParams.get("peek") : null
  }

  // FD-853: the close/re-open race this guards against is real, and it hits users, not just
  // the suite. Closing starts a frame GET, but the drawer hides on its own 200ms slide-out
  // (the cs-drawer's close animation since FD-1311) long before that GET returns — so the same item can be re-opened from the FD-284 cache,
  // instantly and with zero network, while the close is still in flight. When the empty
  // close response finally lands, Turbo empties #peek_panel and turbo:frame-load used to
  // hide the drawer that had just been re-opened. Measured by injection (holding the close
  // render server-side, delays 0-0.5s): 40 of 70 re-opens clobbered before this guard,
  // peaking at 9 of 10; 0 of 70 after it.
  //
  // Two things this deliberately does NOT key on, both measured rather than assumed:
  //
  //   • The URL. Everywhere else in onFrameLoad the URL is the source of truth, but Turbo 8's
  //     FrameController#loadFrameResponse calls changeHistory() BEFORE rendering and before
  //     dispatching turbo:frame-load, so by the time we run, the close has already pushed the
  //     peek-less URL even though the drawer is open on a freshly re-opened item.
  //   • `dismissed`. It looks like the natural signal ("a close set it; only a re-open clears
  //     it") and it is NOT sound: an ordinary open_then_close pair produces a peek_panel
  //     frame-load that still sees content at the ?peek URL, which clears dismissed and
  //     re-reveals, all before the close response arrives. Keying on it left the drawer stuck
  //     open showing the previous item (1-3 errors per peek_cache_test run, 0 on main).
  //
  // So the supersede is recorded when it actually happens: dismiss() (and FD-579's deferred
  // flush) flags that a close navigation is outstanding, and a re-open marks it superseded
  // — in open(), the MISS path, and in #injectFromCache, the zero-network HIT path. Those
  // two are the ONLY arming sites; see #reconcileToUrl for the re-open shape they miss.
  // Only a close whose supersede was recorded gets dropped when its response arrives.
  #restoreSupersededOpen() {
    const id = this.currentId
    const url = new URL(window.location.href)
    url.searchParams.set("peek", id)
    // replaceState, not pushState: we are correcting the entry the close just pushed rather
    // than adding one, and a RAW state ({}, no `turbo` key) keeps it the same shape as every
    // other FD-284 cache-hit entry, so onPopState still owns it. Known cosmetic cost: the
    // corrected entry can duplicate the ?peek entry that preceded the close, so the first
    // Back press is a no-op and the second one closes the drawer. Every state along the way
    // is correct; pushState would instead leave a peek-less entry that Back lands on.
    history.replaceState({}, "", url.toString())
    this.#syncTurboLocation() // FD-1187
    this.#injectFromCache(id)
  }

  // Single dismissal path. The close button is an advancing frame link → let Turbo
  // empty the frame + strip ?peek. Escape/backdrop synthesize that same navigation: they
  // arrive from onDrawerHide with no event, the drawer already closing itself.
  dismiss(event) {
    if (this.dismissed) return
    // FD-579: a dirty multi-select popover (e.g. Assignees) fires its deferred submit
    // when THIS same gesture closes it — serialize that write's morph before the
    // close navigation, or the two Turbo navigations race (see #flushDirtySubmitThenClose).
    if (this.#flushDirtySubmitThenClose(event)) return
    this.dismissed = true
    this.#deactivate()
    this.#hideSkeleton()
    // FD-1311: the originating row is still in the DOM, but it is inert until the modal
    // drawer has closed, so focus returns on cs-after-hide (onDrawerAfterHide).
    this._focusOnHide = true
    this.#slideOutThenHide()
    // FD-853: from here a close frame-GET is NORMALLY outstanding, by either branch below.
    // Normally, not always: [data-peek-close] lives inside the frame CONTENT, so on an
    // empty frame (Escape during a MISS skeleton) the click below finds no link, no close
    // is issued, and the flag is armed regardless. Measured, holding the open GET and
    // pressing Escape on the skeleton: _closePending true, zero close GETs, and it survives
    // the open response — only a close response or #hideImmediately clears it. (When that
    // response lands, onFrameLoad finds content at a ?peek URL and re-reveals the drawer the
    // user just dismissed. Pre-existing and not FD-853 — the flag is not what causes it.)
    //
    // Left armed unconditionally on purpose. Its only consumers are #closeResponse's branch
    // in onFrameLoad, which a real close response has to reach, and a re-open setting
    // _closeSuperseded; and every path that then issues a REAL close clears both flags
    // first (see #slideOutThenHide). Verified end to end: stale arm → cache-HIT re-open
    // (supersede spuriously set) → real Escape → the close fires, is obeyed, and the drawer
    // ends closed at a peek-less URL, identical to a clean run.
    this._closePending = true
    this._closeSuperseded = false
    if (event?.target?.closest?.("[data-peek-close]")) return // close button: Turbo handles the nav
    event?.preventDefault?.()
    const closeLink = this.element.querySelector("[data-peek-close]")
    if (closeLink) closeLink.click() // Escape/backdrop: trigger the same frame+advance nav
  }

  // FD-1311: the drawer is closing. It fires cs-hide for every close, Escape, a backdrop
  // click and our own #closeDrawer alike, and names the same source (its <dialog>) for all
  // three. So our intent decides: if we still want it open, the user dismissed it, and the
  // close navigation has to follow. (Clicks anywhere else can't happen: the page behind the
  // modal is inert. That is what retired FD-833's carve-out for the app nav.)
  onDrawerHide(event) {
    if (event.target !== this.element) return // a cs-* element inside the panel closing
    this._drawerClosing = true
    // FD-1190: we return focus ourselves, without scrolling the list. The drawer would
    // refocus whatever had focus when it opened, with a plain focus() that can scroll.
    // `originalTrigger` is the drawer's own field, not documented API: if it is renamed,
    // this does nothing and the drawer's focus return wins, on the same row.
    this.element.originalTrigger = null
    if (!this._open) return // our own close
    if (this.dismissed) {
      // Already dismissed but somehow still wanted open: settle the state, nothing to send.
      this._open = false
      this.#deactivate()
      return
    }
    this.dismiss()
  }

  onDrawerAfterHide(event) {
    if (event.target !== this.element) return
    this._drawerClosing = false
    if (this._reopenAfterHide) {
      this._reopenAfterHide = false
      this._focusOnHide = false
      this.element.toggleAttribute("open", true)
      return
    }
    if (this._focusOnHide) {
      this._focusOnHide = false
      this.#restoreFocus()
    }
  }

  // FD-284: a whole-page morph (a broadcast refresh from another user, or the actor's
  // own same-url self-edit redirect) means SOME item in this project changed — the
  // signal is payload-free, so we cannot tell which. Drop the entire cache so nothing
  // can ever be served stale, then re-capture the STILL-OPEN item from the freshly
  // morphed, authoritative DOM (keeping it a fresh HIT). #snapshot refuses to capture
  // a mid-edit panel, so the open item is simply left uncached in that
  // case → a clean MISS on next re-open (never a resurrected draft).
  onMorph() {
    if (!this.cache) return
    this.cache.clear()
    if (this.#visible() && this.#hasContent() && this.#frameItemId() === this.currentId) {
      this.#snapshot(this.currentId)
    }
    // FD-1311: the morph re-rendered the drawer from the server, slotted boxes empty.
    if (this.#visible()) this.#syncChrome()
    this.#reflectCacheSize()
  }

  // FD-579: track an in-flight form submission from inside the drawer. The dismiss
  // gestures' capture-phase popover listeners can fire the deferred submit BEFORE our
  // own handler runs (the close-button click), so at dismiss time the popover is no
  // longer dirty — this record is how #flushDirtySubmitThenClose still sees the write.
  //
  // FD-871: submit-START is the ONLY moment at which asking "is this form inside
  // #peek_panel?" is meaningful, so it is the only place that asks. The form is
  // necessarily attached here (Turbo builds a FormSubmission from a real submit event
  // and dispatches this from requestStarted, at the top of FetchRequest#perform), and
  // by submit-END it may not be: #injectFromCache replaces the frame's children, and
  // Turbo's dispatch() util re-targets an event whose target is disconnected onto
  // document.documentElement (turbo 8.0.23, turbo.es2017-esm.js:250-254). Recording
  // identity here — the FormSubmission instance, which Turbo passes as
  // detail.formSubmission on BOTH events (ibid. 1202 / 1248) — is what survives that.
  //
  // FD-284: the item id is captured here too, for the same reason. A successful write
  // from inside the peek changed that item, so its cached panel must be evicted; the id
  // is read from the SUBMITTING form's own panel rather than the mutable currentId, so a
  // drawer that switched items before the response landed never evicts the wrong entry.
  // At submit-end that walk is no longer possible on a detached form — which is exactly
  // when the cached copy is most certainly stale.
  onSubmitStart(event) {
    const itemId = this.#drawerFormItemId(event.target)
    if (itemId === undefined) return
    const key = this.#submissionKey(event)
    if (!key) return
    this._panelSubmissions.set(key, { itemId })
  }

  // FD-1311: the work item a form in the drawer writes to, or undefined for a form outside
  // it. "In the drawer" takes in its header and footer boxes as well as the panel: the
  // footer's Subscribe, archive and votes are as much the item's writes as the panel's own
  // fields, and they sit outside #peek_panel. A box's form writes to the item the box was
  // filled for; a panel form to the panel's item.
  #drawerFormItemId(form) {
    if (!form?.closest || !this.element.contains(form)) return undefined
    const box = form.closest("[data-peek-slotted]")
    if (box) return box.dataset.peekSlottedFor || null
    const panel = form.closest("#peek_panel")
    if (!panel) return undefined
    return panel.querySelector("[data-peek-body]")?.getAttribute("data-peek-item-id") || null
  }

  // The single place that decides whether a finished submission was an in-panel one.
  // FD-579's deferred close is notified from here rather than through a second
  // turbo:submit-end listener of its own, so the two can never disagree.
  onSubmitEnd(event) {
    const submission = this.#takePanelSubmission(event)
    if (!submission) return
    const success = !!event.detail?.success
    if (success) {
      // FD-284: this eviction is the load-bearing fallback for a redirect_back that
      // loses its Referer and lands on a different path — no same-url morph fires, so
      // onMorph never re-snapshots, and this is the only protection.
      if (submission.itemId) this.cache.delete(submission.itemId)
      this.#reflectCacheSize()
    }
    this._panelSubmitWaiter?.(success)
  }

  // FD-871: the stable identity of a form submission across turbo:submit-start and
  // turbo:submit-end. Turbo dispatches both from the same FormSubmission instance, so
  // that object is the key; event.target (the form) is the fallback for a synthetic
  // event that carries no detail.formSubmission.
  #submissionKey(event) {
    return event.detail?.formSubmission || event.target || null
  }

  // FD-871: resolve a turbo:submit-end to the in-panel submission it ends, consuming the
  // record. Returns null when the submission did not start inside the panel.
  //
  // Every submission we tracked is drained: Turbo calls requestFinished from the
  // `finally` of FetchRequest#perform (turbo.es2017-esm.js:811-812), so a submit-start is
  // always followed by a submit-end — including aborts and network errors — and the Map
  // cannot grow without bound. disconnect() clears it anyway.
  //
  // The containment fallback covers a submit-end whose start we never saw (a controller
  // that connected mid-flight). It can only match a form that IS still attached inside
  // the panel, i.e. exactly the case the pre-FD-871 guard got right, so it adds today's
  // behaviour back without re-introducing the hole.
  #takePanelSubmission(event) {
    const key = this.#submissionKey(event)
    if (key && this._panelSubmissions.has(key)) {
      const submission = this._panelSubmissions.get(key)
      this._panelSubmissions.delete(key)
      return submission
    }
    const itemId = this.#drawerFormItemId(event.target)
    return itemId === undefined ? null : { itemId }
  }

  // FD-871: a write started from inside the drawer is still outstanding. Replaces the
  // former _inflightPanelSubmit boolean, whose clear-side guard was the bug.
  #inflightPanelSubmit() {
    return this._panelSubmissions.size > 0
  }

  // FD-284: Turbo owns restore-visits for its OWN history entries (state.turbo set —
  // the cache-MISS opens it advanced). For our RAW pushState hit-entries Turbo does
  // nothing on pop (verified: onPopState → historyPoppedWithEmptyState → history.replace,
  // no visit/fetch/morph), so this handler is the sole actor: reconcile the drawer to
  // the URL, served from cache (zero network) or, if evicted, a cheap fetch.
  onPopState(event) {
    const id = new URL(window.location.href).searchParams.get("peek")
    // No peek in the URL → close the drawer NOW, whoever owns this history entry.
    // (Doing this even for Turbo's own entries avoids relying on a restore-visit to
    // empty the frame in time — that race can leave the drawer showing a stale item.)
    if (!id) {
      this.#deactivate()
      this.#hideImmediately()
      return
    }
    // A peek IS in the URL. Turbo's own (cache-MISS) entries restore-visit and re-render
    // the frame; onFrameLoad reconciles — don't double-handle them.
    if (event.state?.turbo) return
    // Our raw entry: reconcile the drawer to the URL's item.
    this.#reconcileToUrl(id)
  }

  // FD-284/FD-368: make the drawer show the item the URL names, with zero network when
  // possible. Served from the in-memory cache (a HIT), or — if evicted (a morph cleared
  // the cache) or stale — by re-fetching the cheap PeekFrame panel. Used by onPopState
  // (restoring a raw hit-entry) and by onFrameLoad when a stale restore-visit rendered
  // the wrong item at a ?peek URL.
  #reconcileToUrl(id) {
    if (this.#cacheHas(id)) {
      this.#openFromCacheNoPush(id)
      return
    }
    // FD-853 residual hole, known and not closed here: the fetch path below never arms
    // _closeSuperseded — only open() and #injectFromCache do — so a re-open through here
    // (a popstate onto a raw hit-entry whose cache entry was evicted) is unprotected while
    // a close is outstanding. Measured on exactly that pop: _closePending true,
    // _closeSuperseded false. In both orderings tried, the reconcile's own frame visit
    // aborted the close before its response could land (FrameController#visit cancels the
    // frame's current request — turbo.es2017-esm.js:6651). That is ordering luck; nothing
    // in this controller guarantees it.
    this.currentId = id
    this.dismissed = false
    this.#showSkeleton()
    this.#reveal()
    const frame = this.#frame()
    if (!frame) return
    // A turbo-frame only fetches when its src VALUE changes. The hit path advances the
    // URL via raw pushState without touching frame.src, so frame.src can already equal
    // this URL — in which case setAttribute is a no-op and the skeleton would hang.
    // reload() forces the refetch; otherwise set src to trigger the navigation.
    if (frame.getAttribute("src") === window.location.href && typeof frame.reload === "function") {
      frame.reload()
    } else {
      frame.setAttribute("src", window.location.href)
    }
  }

  // --- internals -----------------------------------------------------------

  // FD-579: a multi-select property popover (Assignees/Labels) defers its PATCH to
  // popover close. When the peek is dismissed by the SAME gesture (Escape, backdrop,
  // or the close button — all of which also close the popover via its own document
  // listeners, which can run before or after ours: FD-871's submission tracking covers
  // both orders), that submit and our close navigation become two concurrent Turbo
  // navigations. Whichever loses the race breaks the FD-238 actor
  // contract: either the form's redirect render is dropped (the list row keeps stale
  // avatars — the FD-489 dedup already ate the broadcast copy), or the redirect_back
  // lands last and resurrects ?peek= (its Referer was captured pre-close), reopening
  // the drawer the user just dismissed.
  //
  // Serialize instead of racing: tear the drawer down visually NOW, hold the close
  // navigation, let the popover's submit run its normal same-URL replace visit —
  // Turbo's history still says ?peek=, so it renders as a MORPH, which is exactly the
  // actor's own reconciliation channel (fresh list row, fresh groups) — and only then
  // trigger the close link's frame nav to strip ?peek and empty the frame. While the
  // morph runs, a turbo:before-morph-element guard (the FD-566/FD-568 pattern) keeps
  // the dismissed drawer from being morphed back open. Returns true when it took over.
  #flushDirtySubmitThenClose(event) {
    if (this._flushCleanup) { event?.preventDefault?.(); return true } // already in flight
    if (!this.#dirtyOpenPopover() && !this.#inflightPanelSubmit()) return false
    this.dismissed = true
    // FD-853: a close is intended from here, it is merely deferred until the write's morph.
    // Arm the same supersede bookkeeping dismiss() uses, so a re-open in the meantime is seen.
    this._closePending = true
    this._closeSuperseded = false
    this.#deactivate()
    this.#hideSkeleton()
    this._focusOnHide = true // FD-1311: once the modal drawer has closed (onDrawerAfterHide)
    this.#slideOutThenHide()
    event?.preventDefault?.() // incl. the close-button link — we navigate on finish
    const preserveClosed = (e) => { if (e.target === this.element) e.preventDefault() }
    const finish = () => {
      if (!this._flushCleanup) return
      this._flushCleanup()
      this._flushCleanup = null
      // FD-853: the drawer has been hidden for the whole wait, so the item may have been
      // re-opened before we get here — and firing the close then closes a drawer the user
      // is looking at. Traced on peek_cache_test's deferred-close flow with this guard
      // removed; times from the dismissing gesture, 7 runs across two independent
      // instrumentations:
      //
      //   ~3.0s  the backstop below fires and clicks [data-peek-close]. That click was
      //          itself re-intercepted by this same method, because onSubmitEnd never
      //          cleared the in-flight-submit record (FD-871) — so instead of navigating,
      //          the dismissal machinery ran again and #slideOutThenHide hid the
      //          re-opened drawer ~200ms later (~3.2s). THAT is the route to
      //          peek_cache_test's "Unable to find visible css #peek_panel".
      //   ~6.0s  a second backstop finally let the close GET go, emptying the panel and
      //          dropping ?peek from the URL.
      //
      // FD-871 closed that hole at the source (identity, not containment), so the
      // release below now comes from the write's own morph rather than the backstop.
      // Measured on peek_cache_test's FD-871 flow by probing whether preserveClosed is
      // still installed: the flush is released 1ms after the morph lands with the fix,
      // and stays armed a further 2.6s — waiting out the backstop — without it. The
      // supersede guard below is still what keeps the close from firing at all.
      //
      // Note what is NOT the route, although this comment used to lead with it: the close
      // nav CANCELLING the re-open's own in-flight fetch. On this path the re-open is a
      // cache HIT — the write's morph re-snapshots the open item — so it issues no fetch to
      // cancel, and none was ever observed here, with or without the guard. Cancellation is
      // real on a MISS re-open whose GET is still in flight when the close navigates the
      // same frame: reproduced deliberately (park the re-open's frame GET, then let the
      // close run — Turbo aborts the frame's current request in FrameController#visit,
      // turbo.es2017-esm.js:6651, and the panel ends empty at a peek-less URL). It is just
      // not what this flow does. The in-flight RESPONSE clobber that #closeResponse handles
      // is the same idea one step later. Either way a re-opened drawer means the close is
      // moot: drop it.
      if (this._closeSuperseded) {
        this._closePending = false
        this._closeSuperseded = false
        return
      }
      this._closePending = true
      this._closeSuperseded = false
      this.element.querySelector("[data-peek-close]")?.click()
    }
    // Sequence: the in-panel write's submit-end, then the render it proposed
    // (turbo:morph for the same-URL replace visit; a full swap re-runs connect instead,
    // so the timeout backstops it) — then close. A failed/errored write skips the wait
    // and just closes.
    //
    // FD-871: this waits via onSubmitEnd's waiter hook, NOT its own turbo:submit-end
    // listener. The listener it used to install repeated the closest("#peek_panel")
    // guard, and inherited the same hole: a re-open during the wait replaces the frame's
    // children, so the submitting form is detached by the time the write lands and Turbo
    // dispatches on document.documentElement instead of the form (util `dispatch()` falls
    // back there for a disconnected target — turbo 8.0.23, turbo.es2017-esm.js:250-254).
    // The guard rejected it, onMorph was never registered, and the release came from the
    // 3s backstop (measured 3.01s after the gesture) rather than from the write's morph.
    this._panelSubmitWaiter = (success) => {
      this._panelSubmitWaiter = null
      if (success) {
        document.addEventListener("turbo:morph", onMorph, { once: true })
      } else {
        finish()
      }
    }
    const onMorph = () => finish()
    const timer = setTimeout(finish, 3000) // backstop: never strand the drawer closed at ?peek=
    document.addEventListener("turbo:before-morph-element", preserveClosed)
    this._flushCleanup = () => {
      clearTimeout(timer)
      this._panelSubmitWaiter = null
      document.removeEventListener("turbo:morph", onMorph)
      document.removeEventListener("turbo:before-morph-element", preserveClosed)
    }
    return true
  }

  // An OPEN popover with a pending deferred submit anywhere in the drawer (top-layer
  // dialog popovers can't reach here — the dialog owns Escape/pointer while open).
  #dirtyOpenPopover() {
    const scope = this.hasPanelTarget ? this.panelTarget : this.element
    for (const el of scope.querySelectorAll("[data-controller~='popover']")) {
      const popover = this.application.getControllerForElementAndIdentifier(el, "popover")
      if (popover?.dirty && popover.hasPanelTarget && !popover.panelTarget.hidden) return popover
    }
    return null
  }

  // One document-level capture listener (installed once) that reveals the drawer the
  // instant a peek-open link is clicked, then lets Turbo navigate the frame — UNLESS
  // the item is already cached (FD-284), in which case we paint it from cache with
  // zero network and own the URL ourselves.
  #bindGlobalOpen() {
    if (document.__fdPeekOriginBound) return
    document.__fdPeekOriginBound = true
    const app = this.application
    document.addEventListener(
      "click",
      (event) => {
        // Only true peek-OPEN links (those that target the frame). A link whose
        // href merely carries ?peek= to PRESERVE the open peek across a real list
        // navigation (toolbar/group/filter) must not trigger the optimistic reveal.
        const link = event.target.closest?.("a[data-turbo-frame='peek_panel'][href*='peek=']")
        if (!link) return
        if (event.defaultPrevented || event.button !== 0 ||
            event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
        const wrapper = link.closest("[id^='list_'],[id^='board_'],[data-gantt-item-id],[data-calendar-day]")
        pendingOriginId = wrapper?.id || null
        const container = document.querySelector("[data-controller~='peek']")
        const ctrl = container && app.getControllerForElementAndIdentifier(container, "peek")
        if (!ctrl) return
        // FD-284: re-opening a cached item is served from memory — paint it instantly,
        // zero network. preventDefault aborts Turbo's frame fetch AND its URL advance
        // (we own both on the hit path). A MISS falls through to Turbo's advancing
        // frame nav exactly as before, and is captured on turbo:frame-load.
        const id = new URL(link.href, window.location.href).searchParams.get("peek")
        if (id && ctrl.#cacheHas(id)) {
          event.preventDefault()
          ctrl.#openFromCache(id, link.href)
        } else {
          ctrl.currentId = id
          ctrl.open()
        }
      },
      true
    )
  }

  #frame() {
    return document.getElementById(FRAME_ID)
  }

  // The frame holds a rendered panel (vs. being empty) when its header marker exists.
  #hasContent() {
    const frame = this.#frame()
    return !!frame && frame.querySelector("[data-peek-body]") != null
  }

  // FD-284: the authoritative work-item id of the currently-rendered panel, read from
  // the server-emitted data-peek-item-id on the [data-peek-body] header. null if empty.
  #frameItemId() {
    const body = this.#frame()?.querySelector("[data-peek-body]")
    return body?.getAttribute("data-peek-item-id") || null
  }

  // FD-284: zero-network re-open from the click handler. Captures the focus origin,
  // paints the cached panel, and advances the URL with a RAW history entry (no `turbo`
  // key) so a later pop is absorbed by Turbo and reconciled by onPopState.
  #openFromCache(id, href) {
    this.originId = pendingOriginId
    this.#injectFromCache(id)
    // Raw entry (no `turbo` key). Skip the push when re-clicking the already-open item
    // so we don't stack a duplicate same-URL entry. (A raw push doesn't advance Turbo's
    // own currentIndex; the only consequence is scroll-direction guessing on a later
    // restore-visit — the correct page is still restored by restorationIdentifier.)
    if (href !== window.location.href) history.pushState({}, "", href)
    this.#syncTurboLocation() // FD-1187
  }

  // FD-1187: keep Turbo's own record of the current URL in step with our raw history
  // writes. Turbo never sees a raw pushState/replaceState, so session.history.location
  // stayed on the PREVIOUS URL, and that is exactly what Turbo compares a form's redirect
  // against to decide "same page" (Navigator#getDefaultAction: redirected to location.href
  // → "replace", a morphing refresh; anything else → "advance", a new page). Left stale,
  // the next in-panel edit's redirect_back (to the real URL, from the Referer) read as a
  // new page: <body> replaced, the list's scroll pane reset to the top, and a duplicate
  // history entry pushed.
  //
  // This writes the one field Turbo's own onPopState writes for every pop
  // (`this.location = new URL(window.location.href)`) and nothing else: no `turbo` state
  // on the entry, same restorationIdentifier and currentIndex, so the entry stays raw and
  // our onPopState still owns it. `location` is a public field of Turbo 8's History, not
  // documented API; if an upgrade renames it this is a no-op, and the in-page edit forms'
  // own data-turbo-action="replace" (ApplicationHelper#in_page_edit_data) still holds.
  #syncTurboLocation() {
    const turboHistory = session?.history
    if (turboHistory && "location" in turboHistory) turboHistory.location = new URL(window.location.href)
  }

  // FD-284: zero-network re-open from a popstate (we are already at this history
  // entry, so no pushState; keep the existing focus origin).
  #openFromCacheNoPush(id) {
    this.#injectFromCache(id)
  }

  // FD-284: paint a cached panel into the frame. Replaces the frame's CHILDREN only —
  // never the <turbo-frame target="_top"> host (that would break inline-edit _top
  // redirects). Re-parsing the string re-binds inner Stimulus controllers (block-editor
  // etc.). No turbo:frame-load fires for an innerHTML assignment, so reveal is driven
  // here.
  #injectFromCache(id) {
    const frame = this.#frame()
    const entry = this.cache.get(id)
    if (!frame || !entry) return
    // FD-853: a zero-network re-open. If a close navigation is still outstanding, its
    // response is now stale and must not be allowed to hide what we are about to show.
    if (this._closePending) this._closeSuperseded = true
    this.dismissed = false
    this.currentId = id
    this.#restoreSlots(id, entry.slots) // FD-1311: before the frame, so its observer finds them
    frame.innerHTML = entry.html
    this.#touch(id)
    this.#hideSkeleton()
    this.#reveal()
    this.#reflectCacheSize()
  }

  // FD-284: a valid cached entry exists for this id (presence AND a safety re-check,
  // so a corrupt/dirty entry never serves — defense in depth).
  #cacheHas(id) {
    if (!id || !this.cache?.has(id)) return false
    return this.#cacheEntryValid(this.cache.get(id).html)
  }

  // LRU move-to-newest (Map preserves insertion order).
  #touch(id) {
    const v = this.cache.get(id)
    if (!v) return
    this.cache.delete(id)
    this.cache.set(id, v)
  }

  // LRU evict-oldest down to the cap.
  #evict() {
    while (this.cache.size > CACHE_MAX) {
      this.cache.delete(this.cache.keys().next().value)
    }
  }

  // FD-284: capture the live frame's children into the cache, keyed by `id`, but ONLY
  // when it is a clean, server-authoritative copy (see #cacheEntryValid). Never stores
  // a half-empty frame or a mid-edit panel.
  #snapshot(id) {
    const frame = this.#frame()
    if (!id || !frame) return
    const copy = this.#closedCopy(frame.innerHTML)
    if (!this.#contentValid(copy.content)) return
    this.cache.delete(id)
    this.cache.set(id, { html: copy.innerHTML, slots: this.#slotsFor(id) })
    this.#evict()
    this.#reflectCacheSize()
  }

  // FD-1354: the markup, parsed into a <template>, with anything left open in it closed
  // (lib/cornerstone.js). A refresh snapshots the panel while a popover in it is open (the
  // morph guard keeps it open), and a panel injected with `open` already set brings it back
  // open but deaf to outside clicks and Escape, with a field set to take focus, on every
  // reopen of that item. #snapshot checks the same parse rather than parsing again.
  #closedCopy(html) {
    const tpl = document.createElement("template")
    tpl.innerHTML = html
    closeOverlaysIn(tpl.content)
    return tpl
  }

  // FD-284: validity / safety guard, applied at BOTH capture and read time:
  //  • exactly one identified [data-peek-body] (never cache/serve an empty frame),
  //  • no frozen editor (data-turbo-permanent ⇒ a mid-edit block-editor — would
  //    capture/restore a dirty draft).
  // (It also refused a panel whose FD-98 "Open in modal" dialog was open or loaded;
  // FD-1311 took that dialog out of the peek.)
  #cacheEntryValid(html) {
    if (!html) return false
    const tpl = document.createElement("template")
    tpl.innerHTML = html
    return this.#contentValid(tpl.content)
  }

  #contentValid(content) {
    const body = content.querySelector("[data-peek-body]")
    if (!body || !body.getAttribute("data-peek-item-id")) return false
    if (content.querySelector("[data-turbo-permanent]")) return false
    return true
  }

  // FD-284: test seam — reflect the cache size onto the controller element so system
  // tests can assert the LRU bound without reaching into the Stimulus registry.
  #reflectCacheSize() {
    if (this.cache) this.element.dataset.peekCacheSize = String(this.cache.size)
  }

  // Open the drawer (once; the drawer slides it in). No-op visual when already open.
  #reveal() {
    this.#syncChrome()
    if (this._open) return
    this._open = true
    this.#activate()
    this.#openDrawer()
  }

  // FD-1311: true while the drawer shows, including its close animation (it holds `open`
  // until the animation ends). The old drawer's `!hidden`, which also stayed visible through
  // its slide-out.
  #visible() {
    return this.element.hasAttribute("open")
  }

  // FD-1311: open and close the <cs-drawer> through its reflected `open` attribute, which
  // also works before the element has upgraded. The drawer can't be re-opened during its
  // close animation (it holds `open` true until the animation ends, so setting it again
  // changes nothing), so a re-open then is queued and replayed on cs-after-hide. That
  // window is short and the page is still inert in it, so only Back/Forward and FD-853's
  // repair can land there; a row click can't. If the close already finished and we missed
  // its cs-after-hide, the attribute is gone and the open goes straight through.
  #openDrawer() {
    if (this._drawerClosing && this.#visible()) {
      this._reopenAfterHide = true
      return
    }
    this._drawerClosing = false
    this._reopenAfterHide = false
    this.element.toggleAttribute("open", true)
  }

  #closeDrawer() {
    this._reopenAfterHide = false
    if (this._drawerClosing || !this.#visible()) return
    this.element.toggleAttribute("open", false)
  }

  // FD-1311: the drawer's own chrome follows the item on screen: its label (the dialog's
  // name and its header's title) and its header actions and footer. Until the frame shows
  // the item we're opening (a MISS open's skeleton), they fall back to the default name and
  // empty boxes rather than keeping the previous item's.
  #syncChrome() {
    const current = this.#hasContent() && this.#frameItemId() === this.currentId
    const label = current && this.#frame().querySelector("[data-peek-body]").getAttribute("data-peek-label")
    this.element.setAttribute("label", label || DEFAULT_LABEL)
    this.#stampSlots(current)
  }

  // FD-1311: the frame's content changed: a frame render, a cache injection, or a morph.
  // Only while we want the drawer open: a close response empties the frame while the drawer
  // is still sliding out, and its header and footer should leave with it, not blank first.
  #onFrameChanged() {
    if (this._open) this.#syncChrome()
  }

  // FD-1311: fill the drawer's header-actions and footer boxes (_peek_container) for the
  // current item. A slot only takes the drawer's direct children and the panel is one frame,
  // so a frame load brings them as <template data-peek-slot>s, which are copied in and then
  // removed: copied once, so a later sync can't replace a button the user has focused, and
  // never left to go stale, since a morph doesn't update a template's content. A full page
  // render fills the boxes itself and marks them with the item (data-peek-slotted-for), and
  // a morph keeps them in step, so a box already filled for the current item is left alone.
  #stampSlots(current) {
    const frame = this.#frame()
    for (const box of this.element.querySelectorAll(":scope > [data-peek-slotted]")) {
      const name = box.dataset.peekSlotted
      const tpl = current && frame.querySelector(`:scope > template[data-peek-slot="${name}"]`)
      if (tpl) {
        box.replaceChildren(tpl.content.cloneNode(true))
        box.dataset.peekSlottedFor = this.currentId
        tpl.remove()
      } else if (!current || box.dataset.peekSlottedFor !== this.currentId) {
        box.replaceChildren()
        delete box.dataset.peekSlottedFor
      }
    }
  }

  // FD-284/FD-1311: the boxes' markup for a cache entry, taken only from boxes filled for
  // that item.
  #slotsFor(id) {
    const slots = {}
    for (const box of this.element.querySelectorAll(":scope > [data-peek-slotted]")) {
      if (box.dataset.peekSlottedFor === String(id)) slots[box.dataset.peekSlotted] = this.#closedCopy(box.innerHTML).innerHTML
    }
    return slots
  }

  // FD-284/FD-1311: put a cached entry's header actions and footer back. A box the entry
  // has nothing for is emptied, and #stampSlots then fills it from the frame if it can.
  #restoreSlots(id, slots = {}) {
    for (const box of this.element.querySelectorAll(":scope > [data-peek-slotted]")) {
      const html = slots[box.dataset.peekSlotted]
      if (html == null) {
        box.replaceChildren()
        delete box.dataset.peekSlottedFor
      } else {
        box.innerHTML = html
        box.dataset.peekSlottedFor = String(id)
      }
    }
  }

  #slideOutThenHide() {
    this._open = false
    // FD-853: the invariant is that every path which ARMS a close clears the supersede flag
    // first — NOT that every dismissal funnels through this method, which it does not.
    // Besides connect/disconnect and onFrameLoad's close-response branch, FOUR sites reset
    // the flags around a dismissal, and only two of them get here:
    //   • dismiss()                    — reaches here, then arms _closePending.
    //   • #flushDirtySubmitThenClose() — arms, then reaches here (same gesture).
    //   • finish()                     — does NOT reach here. When it gets as far as the
    //     click at all, the close was not superseded, so nothing has re-opened the drawer
    //     and this.dismissed is still true from the flush: the dismiss() that click triggers
    //     early-returns. It resets inline instead, immediately before the click. (Remove the
    //     supersede guard and that stops holding — measured: at the first click dismissed is
    //     false, dismiss() runs, and the flush re-takes the gesture. That is the chained
    //     backstop traced in finish()'s comment, and it DOES reach here.)
    //   • #hideImmediately()           — does NOT reach here either; it clears both flags
    //     inline, because a close fetch that a newer frame nav CANCELS never dispatches a
    //     turbo:frame-load and nothing else would ever clear them.
    // Resetting here as well as in dismiss() is what fixed the review finding: resetting
    // only in dismiss() left a stale supersede armed across a flush-path dismissal, and the
    // repair then re-opened a drawer the user had just closed (and put ?peek back in the
    // URL) until FD-579's 3s backstop fired.
    this._closeSuperseded = false
    this.#closeDrawer() // the drawer slides out; when the user closed it, it already is
  }

  // Closes without the dismissal's focus return or close navigation. Since FD-1311 the
  // drawer still plays its close animation, so this is "immediate" only in that.
  #hideImmediately() {
    this._open = false
    this.dismissed = true
    // FD-853: closed for real, nothing left to restore. _closePending is cleared here as
    // well because a close fetch that a newer frame navigation CANCELS never produces a
    // turbo:frame-load, so nothing else would ever clear it.
    this._closePending = false
    this._closeSuperseded = false
    this.#deactivate()
    this.#hideSkeleton()
    this.#closeDrawer()
  }

  #showSkeleton() {
    if (this.hasSkeletonTarget) this.skeletonTarget.hidden = false
  }

  #hideSkeleton() {
    if (this.hasSkeletonTarget) this.skeletonTarget.hidden = true
  }

  // FD-1311: while the drawer is open, Escape in a modal or popup over it is theirs
  // (lib/drawer_escape). The drawer itself handles Escape and backdrop clicks, so there is
  // no key or pointer listener of our own any more.
  #activate() {
    if (this._releaseEscape) return
    this._releaseEscape = yieldDrawerEscape()
  }

  #deactivate() {
    this._releaseEscape?.()
    this._releaseEscape = null
  }

  // FD-1190: never scroll the list to move focus. The origin row was on screen when it
  // was clicked, and a plain focus() would still nudge it fully into view.
  #restoreFocus() {
    const el = this.originId ? document.getElementById(this.originId) : null
    if (el) {
      ;(el.querySelector("a, button, [tabindex]") || el).focus?.({ preventScroll: true })
      return
    }
    // FD-1190: no origin row (a ?peek= link, Back/Forward, or the row was removed). Focus
    // the list's own scroll pane: it is always on screen, and arrow keys then scroll the
    // list. The old target, the list's first row, scrolled the list back to the top.
    const pane = this.#listPane()
    if (!pane) return
    if (!pane.hasAttribute("tabindex")) pane.setAttribute("tabindex", "-1")
    pane.focus({ preventScroll: true })
  }

  // FD-1190: every peek surface renders this drawer beside the list's scroll pane.
  #listPane() {
    return Array.from(this.element.parentElement?.children || []).find((el) =>
      el !== this.element && /(auto|scroll)/.test(getComputedStyle(el).overflowY)) || null
  }
}
